/**
 * EnhancedLogisticsTracking.tsx
 * 
 * This component provides a comprehensive logistics tracking system for the farmers marketplace.
 * It allows users to track shipments, verify transporters, and manage logistics operations.
 * The component is divided into three main views: customer, driver, and admin, each with specific functionality.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Map as MapIcon,
  Business as BusinessIcon,
  HealthAndSafety as InsuranceIcon,
  Gavel as LegalIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import CustomerTrackingDashboard from './CustomerTrackingDashboard';
import DriverLocationTracking from './DriverLocationTracking';
import InsuranceVerification from './InsuranceVerification';
import TransporterVerification from './TransporterVerification';
import GovernmentComplianceVerification from './GovernmentComplianceVerification';
import type { Shipment } from './ShipmentTrackingMap';

/**
 * EnhancedLogisticsTracking component that integrates all logistics tracking features
 * Provides a central hub for tracking shipments, verifying transporters, and managing logistics
 * @returns React component for enhanced logistics tracking
 */
const EnhancedLogisticsTracking: React.FC = () => {
  // State for active tab - controls which tab is currently displayed in the interface
  // 0: Track Shipments, 1: Verification Status, 2: About Logistics
  const [activeTab, setActiveTab] = useState(0);
  
  // State for user role - determines which view to display (customer, driver, or admin)
  // This allows the component to simulate different user perspectives for demonstration
  // In a production environment, this would be determined by the user's actual role
  const [userRole, setUserRole] = useState<'customer' | 'driver' | 'admin'>('customer');

  /**
   * Handle tab change - Updates the active tab when user clicks on a different tab
   * This function is passed to the Tabs component's onChange prop
   * 
   * @param event - The React SyntheticEvent triggered by the tab change
   * @param newValue - The index of the newly selected tab (0, 1, or 2)
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Switch user role for demonstration purposes
   * This function allows toggling between different user views to demonstrate
   * how the interface changes based on user role
   * 
   * @param role - The new user role to switch to ('customer', 'driver', or 'admin')
   */
  const handleRoleSwitch = (role: 'customer' | 'driver' | 'admin') => {
    setUserRole(role);
    // Reset active tab when switching roles to ensure a consistent experience
    setActiveTab(0);
  };

  // Sample shipment data for demonstration
  const sampleShipment: Shipment = {
    id: '1',
    status: 'in-transit' as const,
    trackingNumber: 'KE12345678',
    origin: {
      name: 'Nakuru Farmers Cooperative',
      coordinates: {
        lat: -0.3031,
        lng: 36.0800,
      },
    },
    destination: {
      name: 'Nairobi Central Market',
      coordinates: {
        lat: -1.2921,
        lng: 36.8219,
      },
    },
    currentLocation: {
      address: 'Naivasha Road, near Lake Naivasha',
      coordinates: {
        lat: -0.7134,
        lng: 36.4316,
      },
      updatedAt: new Date().toISOString(),
      speed: 65,
    },
    estimatedDelivery: {
      originalDate: '2023-06-15',
      date: '2023-06-15',
      timeWindow: '14:00 - 16:00',
      eta: '15:30',
    },
    driver: {
      id: 'd1',
      name: 'John Kamau',
      phone: '+254712345678',
      rating: 4.8,
    },
    vehicle: {
      type: 'Refrigerated Truck',
      registrationNumber: 'KBZ 123A',
    },
    route: {
      distance: 158,
      duration: 180,
      trafficCondition: 'moderate',
      waypoints: [
        {
          name: 'Naivasha Checkpoint',
          coordinates: {
            lat: -0.7134,
            lng: 36.4316,
          },
          status: 'departed',
          arrivalTime: '12:15',
          departureTime: '12:30',
        },
        {
          name: 'Limuru Weighbridge',
          coordinates: {
            lat: -1.1073,
            lng: 36.6526,
          },
          status: 'pending',
        },
      ],
    },
    updates: [
      {
        timestamp: new Date(Date.now() - 120 * 60000).toISOString(),
        message: 'Shipment has been picked up from Nakuru Farmers Cooperative',
        type: 'info',
      },
      {
        timestamp: new Date(Date.now() - 60 * 60000).toISOString(),
        message: 'Shipment has passed Naivasha checkpoint',
        type: 'info',
      },
    ],
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Main header section with title, description, and role switcher */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Title with icon */}
        <Typography variant="h5" gutterBottom>
          <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Enhanced Logistics Tracking System
        </Typography>
        
        {/* Informational alert describing the system */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Our enhanced logistics tracking system provides real-time tracking, verification of transporters and vehicles, and compliance with government regulations.
        </Alert>
        
        {/* Role switcher buttons for demonstration purposes */}
        {/* In a real application, this would be determined by user authentication */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Button 
            variant={userRole === 'customer' ? 'contained' : 'outlined'}
            onClick={() => handleRoleSwitch('customer')}
          >
            Customer View
          </Button>
          <Button 
            variant={userRole === 'driver' ? 'contained' : 'outlined'}
            onClick={() => handleRoleSwitch('driver')}
          >
            Driver View
          </Button>
          <Button 
            variant={userRole === 'admin' ? 'contained' : 'outlined'}
            onClick={() => handleRoleSwitch('admin')}
          >
            Admin View
          </Button>
        </Box>
      </Paper>
      
      {/* Customer View - Displayed when userRole is 'customer' */}
      {userRole === 'customer' && (
        <>
          {/* Main content paper with tabs */}
          <Paper sx={{ p: 0, mb: 3 }}>
            {/* Tab navigation - allows switching between different customer views */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth" // Makes tabs take full width of container
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {/* Individual tab buttons with icons */}
              <Tab label="Track Shipments" icon={<TruckIcon />} />
              <Tab label="Verification Status" icon={<VerifiedUserIcon />} />
              <Tab label="About Logistics" icon={<InfoIcon />} />
            </Tabs>
            
            {/* Track Shipments Tab Content - Shows real-time shipment tracking */}
            {/* Displayed when activeTab is 0 */}
            {activeTab === 0 && (
              <Box sx={{ p: 0 }}>
                {/* CustomerTrackingDashboard component handles the actual tracking UI */}
                <CustomerTrackingDashboard />
              </Box>
            )}
            
            {/* Verification Status Tab Content - Shows verification requirements */}
            {/* Displayed when activeTab is 1 */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                {/* Section title with icon */}
                <Typography variant="h6" gutterBottom>
                  <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Verification Requirements
                </Typography>
                
                {/* Informational alert explaining verification policy */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  All transporters and logistics companies must meet our verification requirements before being allowed to transport goods.
                </Alert>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Company Registration
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Business Registration" 
                              secondary="Valid business registration certificate" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Tax Compliance" 
                              secondary="Current tax compliance certificate" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Physical Address" 
                              secondary="Verified physical business address" 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <InsuranceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Insurance Requirements
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Vehicle Insurance" 
                              secondary="Comprehensive vehicle insurance" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Goods in Transit" 
                              secondary="Insurance covering goods during transportation" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Public Liability" 
                              secondary="Insurance covering third-party damages" 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <LegalIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Government Compliance
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Transport License" 
                              secondary="Valid transport operator license" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Vehicle Inspection" 
                              secondary="Current vehicle inspection certificate" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Environmental Compliance" 
                              secondary="Environmental standards compliance" 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Driver Verification
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Identity Verification" 
                              secondary="Government-issued ID verification" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Driver's License" 
                              secondary="Valid driver's license for vehicle class" 
                            />
                          </ListItem>
                          
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Background Check" 
                              secondary="Criminal record and driving history check" 
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* About Logistics Tab Content - Provides information about the logistics system */}
            {/* Displayed when activeTab is 2 */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                {/* Section title */}
                <Typography variant="h6" gutterBottom>
                  About Our Logistics System
                </Typography>
                
                {/* Descriptive paragraphs explaining the logistics system */}
                <Typography paragraph>
                  Our enhanced logistics tracking system provides end-to-end visibility of your shipments. We ensure that all transporters and logistics companies meet our strict verification requirements, including insurance coverage and government compliance.
                </Typography>
                
                <Typography paragraph>
                  With real-time tracking, you can monitor your shipments at every stage of the journey. Our system provides traffic updates and ETA information, allowing you to plan accordingly. You can also verify the credentials of the drivers and vehicles transporting your goods.
                </Typography>
                
                <Typography paragraph>
                  For farmers, our logistics system ensures that your produce reaches the market in optimal condition. We provide temperature and humidity monitoring for sensitive cargo, and our verified transporters are trained in handling agricultural products.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Key Features
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Real-Time Tracking
                        </Typography>
                        <Typography variant="body2">
                          Track your shipments in real-time with GPS location updates, traffic information, and ETA calculations.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Verified Transporters
                        </Typography>
                        <Typography variant="body2">
                          All transporters are verified for identity, insurance coverage, and compliance with government regulations.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Secure Shipments
                        </Typography>
                        <Typography variant="body2">
                          Your shipments are secured with comprehensive insurance coverage and handled by verified professionals.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </>
      )}
      
      {/* Driver View - Displayed when userRole is 'driver' */}
      {/* Shows the driver's perspective with location tracking and shipment details */}
      {userRole === 'driver' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <TruckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Driver Dashboard
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Welcome to your driver dashboard. Here you can manage your deliveries, update your location, and communicate with customers.
          </Alert>
          
          <DriverLocationTracking shipment={sampleShipment} />
        </Paper>
      )}
      
      {/* Admin View - Displayed when userRole is 'admin' */}
      {/* Provides administrative tools for verification and compliance management */}
      {userRole === 'admin' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Admin Dashboard
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Welcome to the admin dashboard. Here you can manage transporters, verify documents, and monitor the logistics system.
          </Alert>
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Transporter Verification" icon={<VerifiedUserIcon />} />
            <Tab label="Insurance Verification" icon={<InsuranceIcon />} />
            <Tab label="Government Compliance" icon={<LegalIcon />} />
          </Tabs>
          
          {activeTab === 0 && <TransporterVerification />}
          {activeTab === 1 && <InsuranceVerification />}
          {activeTab === 2 && <GovernmentComplianceVerification />}
        </Paper>
      )}
    </Box>
  );
};

export default EnhancedLogisticsTracking;